import { useParams } from "next/navigation";
import { Fragment } from "react";

import { PlusIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import useModalState from "@logics/hooks/useModalState";
import { useFetchEventCommentListQuery } from "@domains/event/network/eventQueries";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { useCreateCommentMutation } from "@domains/event/network/eventMutations";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { COMMENT_MESSAGE } from "@domains/event/constants/toastMessages";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";

import CommentCard from "../../components/CommentCard/CommentCard";
import CommentForm from "../../components/CommentForm/CommentForm";
import CommentBlocker from "../../components/CommentBlocker/CommentBlocker";
import NoCommentPlaceholder from "../../components/NoCommentPlaceholder/NoCommentPlaceholder";
import ReplyList from "../../components/ReplyList/ReplyList";

import * as S from "./CommentSection.style";

interface CommentSectionProps {
  isHostMyself?: boolean;
  hasJoined?: boolean;
}

const CommentSection = ({ isHostMyself, hasJoined }: CommentSectionProps) => {
  const param = useParams<{ eventId?: string }>();

  const { user, guardRoute } = useAuthSession();
  const { openModal } = useModalState();

  const { data: commentsData } = useFetchEventCommentListQuery(param?.eventId);
  const commentCreateMutation = useCreateCommentMutation();

  const handleOpenCommentForm = () =>
    guardRoute(() => {
      if (!isHostMyself && !hasJoined) {
        ToastError("You can leave a comment after joining this event.");
        return;
      }

      const handleSubmitComment = async (text: string) => {
        if (!param?.eventId) return;

        try {
          await commentCreateMutation.mutateAsync({
            eventId: param?.eventId,
            text,
          });

          ToastSuccess(COMMENT_MESSAGE.COMMENT_ADDED);
        } catch (error) {
          ToastError(ERROR_MESSAGE.SERVER_ERROR);
        }
      };

      openModal({
        CustomComponent: (
          <CommentForm
            headerTitle="Add comment"
            onSubmitComment={handleSubmitComment}
          />
        ),
      });
    });

  return (
    <S.Container $isSelectable={isHostMyself || hasJoined}>
      <S.CommentListContainer>
        {commentsData?.hasComment ? (
          <S.ReplyListContainer>
            {commentsData.commentList.map((comment) => (
              <Fragment key={comment.id}>
                <CommentCard
                  isMySelf={comment.authorId === user?.id}
                  eventId={param?.eventId}
                  commentId={comment.id}
                  authorName={comment.authorName}
                  authorId={comment.authorId}
                  authorProfile={comment.profileImage}
                  commentPayload={comment.content}
                  commentedAt={comment.commentedAt}
                />
                <ReplyList
                  replyList={comment.replyList}
                  currentUserId={user?.id as number}
                  eventId={param?.eventId || ""}
                  commentId={comment.id}
                />
              </Fragment>
            ))}
          </S.ReplyListContainer>
        ) : (
          <NoCommentPlaceholder />
        )}

        <S.AddCommentButton onClick={handleOpenCommentForm}>
          <PlusIcon strokeColor={grayScale.white} />
          <span>Add Comments</span>
        </S.AddCommentButton>
      </S.CommentListContainer>

      {commentsData?.hasComment && !(isHostMyself || hasJoined) && (
        <CommentBlocker />
      )}
    </S.Container>
  );
};

export default CommentSection;
